import { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import GenericCrudTable from "@/components/tables/genericCrudTable";
import type { CrudColumnDef } from "@/components/tables/crudColumnDef";
import { getRecordMetadataColumns } from "@/components/tables/recordMetadataColumns";
import { Link } from "react-router-dom";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import styles from "@/components/subjectOverview/subjectOverviewTodos.module.css";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import TodoCreateEditModal from "@/components/todos/todoCreateEditModal";
import GenericNotification from "@/components/notification/genericNotification";
import { replaceDate } from "@/helper/dateHandling";
import { todoEntityAdapter } from "@/components/todos/todoEntityAdapter";

interface Props {
  detailedSubject?: any;
  openWithSpecificSubjectIdUrl?: boolean;
}

const SubjectOverviewTodos: FC<Props> = ({
  detailedSubject,
  openWithSpecificSubjectIdUrl = false,
}) => {
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [refreshTable, setRefreshTable] = useState(false);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const { t } = useTranslation();

  const subjectOption = detailedSubject
    ? { value: detailedSubject.id, label: detailedSubject.name }
    : undefined;

  const customTopToolbarAction = () => {
    const subjectId = detailedSubject?.id;
    const linkTo =
      openWithSpecificSubjectIdUrl && subjectId
        ? `/admin/todoList/${subjectId}`
        : `/admin/todoList`;
    return (
      <GenericButton startIcon={<OpenInNewIcon />}>
        <Link
          to={linkTo}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.link}
        >
          {t("todo_multi")}
        </Link>
      </GenericButton>
    );
  };
  const columns = useMemo<CrudColumnDef[]>(
    () => [
      {
        accessorKey: "id",
        header: t("id"),
        id: "id",
      },
      {
        header: t("due_date"),
        accessorKey: "due_date",
        id: "due_date",
        dateTimePicker: true,
        enableForCreate: true,
        required: true,
        errorMessage: t("field_required"),
        filterVariant: "date",
        sortingFn: "datetime",
        Cell: ({ cell }: any) => {
            return <span>{replaceDate(cell.getValue(), true, true)}</span>;
        },
      },
      {
        header: t("title"),
        accessorKey: "title",
        id: "title",
      },
      {
        header: t("description"),
        accessorKey: "description",
        id: "description",
        /* eslint-disable react/prop-types */
        Cell: ({ cell, row }: any) => {
          const description = cell.getValue();
          const type = row.original.type;
          if (
            type === "gbu" &&
            typeof description === "object" &&
            description !== null
          ) {
            return (
              <div>
                <div>
                  <span className={styles.boldText}>
                    {t("assessment_gbu_label")}:
                  </span>
                </div>
                <div className={styles.marginBottom}>
                  {description.assessment || ""}
                </div>
                <div>
                  <span className={styles.boldText}>
                    {t("dangerous_answer_gbu_label")}:
                  </span>
                </div>
                <div className={styles.marginBottom}>
                  {description.dangerous_answer || ""}
                </div>
                <div>
                  <span className={styles.boldText}>
                    {t("possible_remedy_gbu_label")}:
                  </span>
                </div>
                <div>{description.possible_remedy || ""}</div>
              </div>
            );
          }
          return description || "";
        },
      },
      {
        header: t("done"),
        accessorKey: "done",
        id: "done",
        filterVariant: "select",
        filterSelectOptions: [
          { value: "true", label: t("yes") },
          { value: "false", label: t("no") },
        ],
        setNoFilterParameter: true,
        accessorFn: (row: any) => {
          if (row.done === true) return t("yes");
          else return t("no");
        },
      },
      ...getRecordMetadataColumns(t, ["created_on", "created_by", "modified_on", "modified_by"]),
    ],
    [],
  );

  return (
    <>
      <GenericCrudTable
        entity={"todo"}
        entityAdapter={todoEntityAdapter}
        columns={columns}
        customTopToolBarAction={() => customTopToolbarAction()}
        customGlobalFilterPosition={"left"}
        customDensity="compact"
        customPageSize={5}
        showRowActions={false}
        searchId={detailedSubject?.id}
        tableCollapseWidth={"75%"}
        customCreateHandler={() => {
          setShowSuccessNotification(false);
          setOpenCreateModal(true);
        }}
        refreshTable={refreshTable}
      />
      <TodoCreateEditModal
        open={openCreateModal}
        onClose={() => setOpenCreateModal(false)}
        onSuccess={() => {
          setRefreshTable((prev) => !prev);
          setShowSuccessNotification(true);
        }}
        preExistingSubject={subjectOption}
      />
      <GenericNotification
        message={
          showSuccessNotification ? t("todo_created_successfully") : null
        }
        variant="success"
        handleCloseSnackbar={() => setShowSuccessNotification(false)}
      />
    </>
  );
};

export default SubjectOverviewTodos;
